// MessageCategoryFeedsById.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';

import Tabs from '../component/Tabs.jsx';
import Tab from '../component/Tab.jsx';

import media from '../style/media.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import { breakpoint, headerHeight, color } from '../style/variables.js';
import BackIconSource from '../../img/ic_topbar_back.svg';
import { CategoryListType } from '../resource/categoryConstants.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { ButtonId } from '../resource/mixpanel.js';
import { generateLangPath, getLangFromPath } from '../resource/langPath.js';

const tabMetadata = {
  [CategoryListType.ALL]: {
    i18nKey: 'tab_all',
    buttonId: ButtonId.Category.TabAll,
  },
  [CategoryListType.STORY]: {
    i18nKey: 'stories',
    buttonId: ButtonId.Category.TabStory,
  },
  [CategoryListType.FLIX]: {
    i18nKey: 'title_flix',
    buttonId: ButtonId.Category.TabFlix,
  },
  [CategoryListType.SHORT]: {
    i18nKey: 'shorts',
    buttonId: ButtonId.Category.TabShort,
  },
  [CategoryListType.IMAGE]: {
    i18nKey: 'profile_photo',
    buttonId: ButtonId.Category.TabImage,
  },
};
const tabsStyle = {
  justifyContent: 'start',
};
const tabStyle = {
  marginRight: '28px',
  flex: 'none',
};
const lastTabStyle = {
  ...tabStyle,
  marginRight: 0,
};
const tabLabelStyle = {
  margin: 0,
  opacity: 1,
};

class MessageCategoryFeedsById extends React.PureComponent {
  handleTabChange = (event, value) => {
    event?.preventDefault();
    this.props.replace(this.getPath(value));
  };

  getPath = tabValue => {
    const { location } = this.props;
    const searchParams = new URLSearchParams(location.search);
    ['feed', '_feed', 'filters', '_filters', 'sorts', '_sorts'].forEach(key =>
      searchParams.delete(key)
    );

    const { categoryTabs, categoryId } = this.props;
    const listType = categoryTabs.includes(tabValue)
      ? tabValue
      : categoryTabs[0];

    let basePath = `/category/${categoryId}`;
    if (listType !== CategoryListType.ALL) {
      basePath += `/${listType}`;
    }

    const currentLang = getLangFromPath(location.pathname);
    const pathname = generateLangPath(basePath, currentLang);

    return {
      pathname,
      search: searchParams.toString() ? `?${searchParams.toString()}` : '',
    };
  };

  renderTabs = () => {
    const { deviceWidth, t, categoryTabs, listType } = this.props;
    const isOnMobile = deviceWidth <= breakpoint.mobile;
    if (!isOnMobile) tabsStyle.paddingLeft = 20;

    return (
      <TabsWrapper>
        <Tabs
          value={listType}
          onChange={this.handleTabChange}
          isScrollable={isOnMobile}
          style={tabsStyle}
        >
          {(categoryTabs || []).map((tabKey, index) => {
            const tabData = tabMetadata[tabKey];
            if (!tabData) return null;
            const isActive = tabKey === listType;
            const title = t(tabData.i18nKey, {
              ns: TranslationNamespace.FEED,
            });
            const { pathname, search } = this.getPath(tabKey);
            const tabHref = `${pathname}${search}`;
            return (
              <Tab
                key={tabKey}
                value={tabKey}
                style={
                  categoryTabs.length - 1 === index ? lastTabStyle : tabStyle
                }
                data-element_id={tabMetadata[tabKey].buttonId}
                labelStyle={tabLabelStyle}
                label={<TabText isActive={isActive}>{title}</TabText>}
                href={tabHref}
              />
            );
          })}
        </Tabs>
      </TabsWrapper>
    );
  };

  render() {
    const { goBack, t, categoryId, children } = this.props;
    return (
      <Container>
        <Header>
          <BackButton onClick={goBack}>
            <img src={BackIconSource} alt="go back" height={32} width={32} />
          </BackButton>
          <HeaderText>
            {t(`category_${categoryId}`, {
              ns: TranslationNamespace.FEED,
            })}
          </HeaderText>
        </Header>
        {this.renderTabs()}
        <TabContent>{children}</TabContent>
      </Container>
    );
  }
}

MessageCategoryFeedsById.propTypes = {
  deviceWidth: PropTypes.number,
  replace: PropTypes.func,
  goBack: PropTypes.func,
  location: PropTypes.object,
  t: PropTypes.func.isRequired,
  categoryTabs: PropTypes.array,
  categoryId: PropTypes.string,
  listType: PropTypes.string,
  children: PropTypes.node,
};

MessageCategoryFeedsById.defaultProps = {
  deviceWidth: 0,
  replace: () => null,
  goBack: () => null,
  categoryTabs: [],
  location: {},
  categoryId: '',
  listType: null,
  children: null,
};

const Container = styled.div`
  position: relative;
  margin: calc(${headerHeight + 20}px + var(--space-to-pinned-notification))
    auto 0;
  max-width: 962px;

  border-right: 1px solid var(--background-color-secondary-on-dark, #232323);
  border-left: 1px solid var(--background-color-secondary-on-dark, #232323);
  background-color: var(--background-color-primary-on-dark, #191919);

  ${media.mobile`
    margin: unset;
    width: 100%;
    border-right: unset;
    border-left: unset;
  `};
`;

const Header = styled.div`
  background-color: ${color.black};
  position: relative;
  padding: 8px 20px;
  ${media.mobile`
    padding: 16px;
    border-bottom: 1px solid rgba(71, 71, 71, 0.40);
    position: fixed;
    top: var(--space-to-pinned-notification);
    left: 0;
    right: 0;
    z-index: 2;
    background-color: #191919;
    display: flex;
    align-items: center;
  `}
`;

const HeaderText = styled.h1`
  font-size: 22px;
  font-weight: 600;
  line-height: 150%;
  margin: 0;
  padding: 0;
  ${media.mobile`
    font-size: 16px;
  `}
`;

const BackButton = styled.button`
  ${ResetButtonStyle}
  display: none;
  width: 32px;
  height: 32px;
  ${media.mobile`
    display: block;
    margin-right: 8px;
  `}
`;

const TabsWrapper = styled.div`
  position: sticky;
  top: calc(${headerHeight}px + var(--space-to-pinned-notification));
  z-index: 2;
  background-color: ${color.black};
`;

const TabText = styled.h2`
  font-size: 16px;
  margin: 0;
  font-weight: normal;
  ${media.mobile`
    font-size: 14px;
  `};
`;

const TabContent = styled.div`
  background-color: ${color.black};
  ${media.mobile`
    padding-top: calc(${headerHeight + 20}px + var(--space-to-pinned-notification));
  `};
`;

export default withTranslation()(MessageCategoryFeedsById);
